<template>
	<div class="room">
		<div v-if="isLoading" class="spinner-container">
			<md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
			<p class="md-subheading loading-text">Loading room data...</p>
		</div>

		<md-empty-state
			v-else-if="isIncorrectVersion"
			class="md-accent"
			md-icon="warning"
			md-label="Oops!"
			md-description="You are viewing an old version of this app. Please refresh your browser."
		></md-empty-state>

		<div v-else-if="isRoomEmpty" class="empty-room">
			<p>Room is empty. Be first to start it!</p>
			<md-button class="md-dense md-raised md-primary" type="button" @click="createRoom">Create Room</md-button>
		</div>

		<div v-else-if="isConfirmCreateRoom" class="active-room">
			<p>Are you sure you want to reset the game?</p>

			<div class="buttons">
				<md-button class="md-dense md-raised md-primary" type="button" @click="cancelCreateRoom">No, go back</md-button>
				<md-button class="md-dense md-raised md-accent" type="button" @click="confirmCreateRoom">Yes, reset game</md-button>
			</div>
		</div>

		<div v-else class="active-room">
			<p class="md-display-1" v-if="!roomData.isStarted">Waiting...</p>
			<p class="md-display-1" v-else-if="!roomData.isComplete" :class="getUserCSS()">Game started!</p>
			<p class="md-display-1" v-else-if="roomData.didVillageSucceed" :class="getRoleCSS('villager')">Village wins!</p>
			<p class="md-display-1" v-else :class="getRoleCSS('mafia')">Mafia wins!</p>

			<md-list v-if="sortedPlayersByPoints.length > 0">
				<md-list-item v-for="player in sortedPlayersByPoints" :key="player.username" :class="canVotePlayer(player) ? 'votable' : ''" @click="votePlayer(player)">
					<md-icon v-if="getPlayerIcon(player)" :class="getPlayerCSS(player)">{{ getPlayerIcon(player) }}</md-icon>
					<span class="md-list-item-text" :class="getPlayerCSS(player)">
						<div class="player-text">
							<span class="player-text-name">{{ player.username }}</span>
							<span class="player-text-points">{{ player.points }}</span>
						</div>
					</span>
				</md-list-item>
			</md-list>

			<template v-if="!roomData.isStarted">
				<template v-if="players.length < 3">
					<template v-if="!isInRoom">
						<p>A new room has been created and they need at least 3 players to play.</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-primary" type="button" @click="joinRoom">Join Room</md-button>
							<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
						</div>
					</template>
					<template v-else>
						<p>Waiting for at least 3 players before game can be started.</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-accent" type="button" @click="leaveRoom">Leave Room</md-button>
							<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
						</div>
					</template>
				</template>
				<template v-else>
					<template v-if="!isInRoom">
						<p>A new room has been created. Join before they start!</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-primary" type="button" @click="joinRoom">Join Room</md-button>
							<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
						</div>
					</template>
					<template v-else-if="!isRoomLeader">
						<p>Waiting on <span :class="getRoleCSS('villager')">{{ leaderUsername }}</span> to start the game.</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-accent" type="button" @click="leaveRoom">Leave Room</md-button>
							<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
						</div>
					</template>
					<template v-else>
						<p>Waiting on you to start the game.</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-primary" type="button" @click="runRoom">Start Game</md-button>
							<md-button class="md-dense md-raised md-accent" type="button" @click="leaveRoom">Leave Room</md-button>
						</div>
					</template>
				</template>
			</template>
			<template v-else-if="!roomData.isComplete">
				<template v-if="!isInRoom">
					<div class="buttons">
						<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
					</div>
				</template>
				<template v-else-if="userRole == 'villager'">
					<p>You are a <span :class="getUserCSS()">Villager</span>.</p>
					<p>Vote for who you think is the mafia.</p>

					<template v-if="hasUserVoted">
						<p><span :class="getRoleCSS('villager')">{{ votes.length }}/{{ players.length - 1 }}</span> have voted who they think is the mafia.</p>
						<p :class="getRoleCSS('mafia')" v-if="roomData.hasMafiaReportedSuccess">The mafia has reported whether they won or lost.</p>
						<p :class="getRoleCSS('mafia')" v-else>Waiting on mafia to report their success.</p>
					</template>

					<div class="buttons">
						<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
					</div>
				</template>
				<template v-else-if="userRole == 'mafia'">
					<template v-if="!roomData.hasMafiaReportedSuccess">
						<p>You are the <span :class="getUserCSS()">Mafia</span>!</p>
						<p>Did you succeed in losing the game?</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-primary" type="button" @click="setMafiaSuccess">Yes</md-button>
							<md-button class="md-dense md-raised md-accent" type="button" @click="setMafiaFailed">No</md-button>
						</div>
					</template>
					<template v-else>
						<p><span :class="getRoleCSS('villager')">{{ votes.length }}/{{ players.length - 1 }}</span> have voted who they think is the mafia.</p>

						<div class="buttons">
							<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
						</div>
					</template>
				</template>
			</template>
			<template v-else>

				<p v-for="vote in votes" :key="vote.voter">
					<span :class="getRoleCSS('villager')">{{ vote.voter }}</span> voted for <span :class="getRoleCSS('mafia')">{{ vote.target }}</span>
				</p>

				<p :class="getRoleCSS('mafia')">{{ mafiaUsername }} was the mafia!</p>

				<p :class="getRoleCSS('villager')" v-if="roomData.didVillageSucceed">Village wins!</p>
				<p :class="getRoleCSS('mafia')" v-else>Mafia Wins!</p>

				<template v-if="!isInRoom || !isRoomLeader">
					<div class="buttons">
						<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
					</div>
				</template>
				<template v-else>
					<div class="buttons">
						<md-button class="md-dense md-raised md-primary" type="button" @click="recreateRoom">Play Again</md-button>
						<md-button class="md-dense md-raised md-accent" type="button" @click="createRoom">Reset Game</md-button>
					</div>
				</template>
			</template>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	name: 'room',
	data() {
		return {
			isConfirmCreateRoom: false
		}
	},
	computed: {
		...mapState([
			'username',
			'roomData',
			'players',
			'votes',
		]),
		...mapGetters([
			'isLoading',
			'isIncorrectVersion',
			'isRoomEmpty',
			'isInRoom',
			'isRoomLeader',
			'sortedPlayersByPoints',
			'userRole',
			'leaderUsername',
			'mafiaUsername',
			'canVotePlayer',
			'hasUserVoted',
		]),
	},
	methods: {
		getPlayerCSS(player) {
			if (player.username == this.username || this.roomData.isComplete && player.role == 'mafia') {
				return this.getRoleCSS(player.role)
			}
		},
		getPlayerIcon(player) {
			if (player.username != this.username) {
				if (this.isInRoom && this.roomData.isStarted && !this.roomData.isComplete && this.userRole == 'villager') {
					return this.votes.some(vote => vote.voter == this.username && vote.target == player.username) ? 'check_box' : 'check_box_outline_blank'
				}
			}

			return 'person'
		},
		getUserCSS() {
			return this.getRoleCSS(this.userRole)
		},
		getRoleCSS(role) {
			if (role == 'mafia') return 'mafia'
			if (role == 'villager') return 'villager'
		},
		createRoom() {
			if (this.isRoomEmpty) {
				this.$store.dispatch('createRoom')
			} else {
				this.isConfirmCreateRoom = true
			}
		},
		cancelCreateRoom() {
			this.isConfirmCreateRoom = false
		},
		confirmCreateRoom() {
			this.isConfirmCreateRoom = false
			this.$store.dispatch('createRoom')
		},
		recreateRoom() {
			this.$store.dispatch('recreateRoom')
		},
		joinRoom() {
			this.$store.dispatch('joinRoom')
		},
		leaveRoom() {
			this.$store.dispatch('leaveRoom')
		},
		runRoom() {
			this.$store.dispatch('runRoom')
		},
		votePlayer(player) {
			if (this.canVotePlayer(player)) {
				this.$store.dispatch('vote', player.username)
			}
		},
		setMafiaSuccess() {
			if (this.userRole == 'mafia') {
				this.$store.dispatch('setMafiaSuccess', true)
			}
		},
		setMafiaFailed() {
			if (this.userRole == 'mafia') {
				this.$store.dispatch('setMafiaSuccess', false)
			}
		}
	}
}
</script>

<style scoped>
/* Loading state */
.spinner-container {
	align-items: center;
	display: flex;
	flex-flow: column;
}

/* Empty room state */
.empty-room {
	display: flex;
	flex-flow: column;
}

.empty-room button {
	margin-left: 0;
	margin-right: 0;
}

/* Active room state */
.active-room .md-display-1 {
	color: #FFF;
	margin: 0 0 15px;
}

.active-room .buttons {
	display: flex;
	flex-flow: row wrap;
}

.active-room .buttons button {
	margin-left: 0;
	margin-right: 10px;
}



.player-text { align-items: center; display: flex; justify-content: space-between; }
.player-text .player-text-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: auto; }
.player-text .player-text-points { margin-left: 40px; width: auto; }
.votable { cursor: pointer; }
.mafia { color: #ff5252 !important; }
.villager { color: #448aff !important; }
</style>
